import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, Wrench, LifeBuoy } from 'lucide-react';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';

// Bridge visualisation — Consulting roots (left) to AI-native (right).
// A single SVG motif that reads at a glance: two islands connected by a
// growing arc, with the NeuralWorks core sitting in the middle.
function BridgeMotif() {
  return (
    <div className="relative w-full">
      <svg viewBox="0 0 640 240" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="bridgeArc" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0F3D22" stopOpacity="0.35" />
            <stop offset="50%" stopColor="#22A94F" />
            <stop offset="100%" stopColor="#7ED4A0" />
          </linearGradient>
          <radialGradient id="coreGlow" r="60%">
            <stop offset="0%" stopColor="#22A94F" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#22A94F" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Left island — consulting roots */}
        <motion.g
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <ellipse cx="90" cy="180" rx="80" ry="14" fill="#1A1A1A" fillOpacity="0.05" />
          <circle cx="90" cy="150" r="42" fill="#F4F2EC" stroke="#0F3D22" strokeWidth="1.5" />
          <text x="90" y="145" textAnchor="middle" style={{ fontSize: 10, fill: '#0F3D22', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>SINCE</text>
          <text x="90" y="163" textAnchor="middle" style={{ fontSize: 22, fill: '#0F3D22', fontFamily: 'Fraunces, serif', fontWeight: 500 }}>2020</text>
        </motion.g>

        {/* Right island — AI-native today */}
        <motion.g
          initial={{ opacity: 0, x: 12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <ellipse cx="550" cy="180" rx="80" ry="14" fill="#22A94F" fillOpacity="0.10" />
          <circle cx="550" cy="150" r="42" fill="#22A94F" fillOpacity="0.14" stroke="#22A94F" strokeWidth="1.5" />
          <text x="550" y="145" textAnchor="middle" style={{ fontSize: 10, fill: '#178838', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>TODAY</text>
          <text x="550" y="163" textAnchor="middle" style={{ fontSize: 13, fill: '#178838', fontFamily: 'Fraunces, serif', fontWeight: 500 }}>AI-native</text>
        </motion.g>

        {/* Arc bridge */}
        <motion.path
          d="M 130 150 Q 320 20 510 150"
          stroke="url(#bridgeArc)"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: 'easeInOut', delay: 0.3 }}
        />

        {/* Milestone dots along the arc */}
        {[
          { t: 0.2, label: 'Advisory' },
          { t: 0.5, label: 'Engineering' },
          { t: 0.8, label: 'AI-native' },
        ].map((m, i) => {
          const angle = Math.PI * (1 - m.t);
          const cx = 320 - 190 * Math.cos(angle);
          const cy = 150 - 130 * Math.sin(angle);
          return (
            <motion.g
              key={i}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 + i * 0.18 }}
            >
              <circle cx={cx} cy={cy} r="5" fill="#FBFAF6" stroke="#22A94F" strokeWidth="1.5" />
              <text x={cx} y={cy - 12} textAnchor="middle" style={{ fontSize: 10, fill: '#0F3D22', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 600 }}>
                {m.label}
              </text>
            </motion.g>
          );
        })}

        {/* Core signet — NeuralWorks framework — sits in the middle */}
        <motion.g
          initial={{ opacity: 0, scale: 0.6 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.3, duration: 0.5, ease: 'backOut' }}
        >
          <circle cx="320" cy="30" r="26" fill="url(#coreGlow)" />
          <circle cx="320" cy="30" r="14" fill="#0F3D22" />
          <text x="320" y="34" textAnchor="middle" style={{ fontSize: 9, fill: '#7ED4A0', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>NW</text>
        </motion.g>
      </svg>
    </div>
  );
}

// Three pillar cards inspired by the reference site — Advisory / Engineering / Support
// but shown through the AI-native lens.
const PILLARS = [
  {
    id: '01',
    title: 'Advisory & Strategy',
    lede: 'Where AI will pay for itself in your estate — and where it will not.',
    body: 'Digital strategy, product management, program leadership and CX design. The consulting muscle we built for a decade, now paired with a hard-nosed view of what actually works with AI.',
    capabilities: ['Digital Strategy', 'Product Management', 'CX / UX / UI', 'Program Management', 'AI Opportunity Mapping'],
    icon: <Compass size={20} />,
    accent: '#22A94F',
    halo: 'rgba(34,169,79,0.14)',
  },
  {
    id: '02',
    title: 'Engineering & Delivery',
    lede: 'Agent-assisted delivery under the NeuralWorks discipline.',
    body: 'Full-stack engineering, cloud transformation and QA automation — reshaped around Tests-as-Spec, shadow data and the two-person release rule. The output is working software, not slideware.',
    capabilities: ['Full-Stack Engineering', 'Cloud & Data Platforms', 'Agent-Assisted Development', 'QA & Automation', 'Mobile & Commerce'],
    icon: <Wrench size={20} />,
    accent: '#5B7BB8',
    halo: 'rgba(91,123,184,0.16)',
  },
  {
    id: '03',
    title: 'Run & Support',
    lede: 'Managed delivery with AI-assisted triage baked in.',
    body: 'L1–L3 support, DevOps, WebOps and BizOps — augmented by scoped agents that classify, contain and propose fixes against shadow data. Every production change still passes through two human reviewers.',
    capabilities: ['Tech Support (L1/L2/L3)', 'DevOps & CI/CD', 'WebOps & BizOps', 'Managed Services', 'Agent-Assisted Triage'],
    icon: <LifeBuoy size={20} />,
    accent: '#E6A83A',
    halo: 'rgba(230,168,58,0.18)',
  },
];

const TIMELINE = [
  { year: '2020', label: 'Founded', body: 'DigitalWorks Consulting Group opens in Dubai. Advisory for global brands entering MEA.' },
  { year: '2021', label: 'Engineering', body: 'The delivery muscle scales — full-stack engineering practice built across the UAE and India.' },
  { year: '2023', label: 'Regional depth', body: 'Presence stretches into the EU and US. Managed services and support practice matures.' },
  { year: '2025', label: 'NeuralWorks', body: 'We rebuild how we deliver around agent tooling. The framework is stress-tested on our own estate first.' },
  { year: '2026', label: 'AI-native today', body: 'Every engagement runs under the same discipline — advisory, engineering, run — with agents inside every phase, humans on every release.' },
];

const STATS = [
  { v: '5+', l: 'Years shipping enterprise software' },
  { v: '200+', l: 'Engineers & delivery consultants' },
  { v: '4', l: 'Continents of active delivery' },
  { v: '350+', l: 'Global engagements delivered' },
];

export default function About() {
  return (
    <div className="bg-[#FBFAF6]">
      <SEO
        title="About — a consulting group that rebuilt itself around AI"
        description="Founded in Dubai in 2020, DigitalWorks is the AI-native transformation and engineering practice of Ansar Kanhiroli. From a first brief in the Middle East to a distributed team shipping working software today — how we came to be."
        canonical="https://digitalworks.co/about"
      />
      {/* HERO */}
      <section className="relative pt-16 pb-8 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.14), transparent)' }}
        />
        <div className="dwx relative">
          <div className="relative h-[2px] w-[220px] bg-[#22A94F] mb-8" />
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">About DigitalWorks</div>
          <h1 className="serif-tight text-[54px] md:text-[92px] leading-[0.94] mt-4 max-w-5xl">
            A consulting group that
            <br />
            <span className="text-[#22A94F]">rebuilt itself around AI.</span>
          </h1>

          <div className="mt-14 grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-6 text-[17px] leading-relaxed text-[#1A1A1A]/85 space-y-5">
              <p>
                We started in 2020 as a boutique digital consultancy in Dubai &mdash; helping global brands and regional groups design, engineer and run digital experiences that behaved themselves in production.
              </p>
              <p>
                A decade of consulting shaped the muscle. Then agent tooling got good enough that our delivery model stopped making sense as it was. We had a choice: bolt AI onto what we already did, or rebuild how we deliver around it.
              </p>
              <p className="text-[#1A1A1A] font-medium">We rebuilt. NeuralWorks is the result &mdash; and we ran it on ourselves before we offered it to anyone else.</p>
            </div>
            <div className="col-span-12 md:col-span-6">
              <BridgeMotif />
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-16 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-10">By the numbers</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="border-t-2 border-[#22A94F] pt-6"
              >
                <div className="serif-tight text-[64px] leading-none text-[#1A1A1A]">{s.v}</div>
                <div className="mt-4 text-[13px] leading-relaxed text-[#1A1A1A]/70">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-24">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">What we do, end-to-end</div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.95] text-[#1A1A1A]">
                Three pillars.
                <br />
                <span className="text-[#1A1A1A]/50">One accountable team.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[15px] leading-relaxed text-[#1A1A1A]/70">
                From the first strategy conversation to the running system four years later &mdash; the same people, under one discipline, augmented by agents.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group rounded-[24px] bg-[#FBFAF6] border border-[#1A1A1A]/10 p-8 relative overflow-hidden lift"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-20 -right-20 w-[240px] h-[240px] rounded-full transition-opacity opacity-70 group-hover:opacity-100"
                  style={{ background: `radial-gradient(closest-side, ${p.halo}, transparent)` }}
                />
                <div className="relative flex items-start justify-between">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: `${p.accent}1A`, color: p.accent }}>
                    {p.icon}
                  </div>
                  <span className="mono text-[10px] tracking-[0.22em] uppercase" style={{ color: p.accent }}>{p.id}</span>
                </div>
                <div className="relative serif text-[26px] leading-tight mt-6 text-[#1A1A1A]">{p.title}</div>
                <div className="relative mt-2 text-[14px] font-medium" style={{ color: p.accent }}>{p.lede}</div>
                <p className="relative mt-4 text-[14px] leading-relaxed text-[#1A1A1A]/75">{p.body}</p>
                <div className="relative mt-6 flex flex-wrap gap-2">
                  {p.capabilities.map((c) => (
                    <span key={c} className="mono text-[10px] px-2.5 py-1 rounded-full bg-[#F4F2EC] border border-[#E8E4D9] text-[#1A1A1A]/75">{c}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE — the pivot */}
      <section className="py-24 bg-[#1A1A1A] text-[#FBFAF6] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(34,169,79,0.14), transparent)' }}
        />
        <div className="dwx relative">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">The pivot</div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.95]">
                Six years of consulting,
                <br />
                <span className="text-[#FBFAF6]/50">rebuilt around one framework.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[14px] leading-relaxed text-[#FBFAF6]/70">
                The moves that mattered &mdash; not a corporate timeline, a delivery record.
              </p>
            </div>
          </div>

          {/* Timeline rail */}
          <div className="relative">
            {/* Rail line */}
            <div className="absolute top-6 left-0 right-0 h-[2px] bg-[#22A94F]/25 hidden md:block" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 relative">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative"
                >
                  {/* Node */}
                  <div className="relative flex items-center gap-3 mb-4">
                    <div className="relative">
                      <div className="w-3 h-3 rounded-full bg-[#22A94F]" />
                      <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#22A94F]/40 animate-ping" style={{ animationDelay: `${i * 0.4}s` }} />
                    </div>
                    <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#7ED4A0]">{t.year}</div>
                  </div>
                  <div className="serif text-[22px] leading-tight text-[#FBFAF6]">{t.label}</div>
                  <p className="mt-3 text-[13px] leading-relaxed text-[#FBFAF6]/70">{t.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUNDER — warm, humanised section */}
      <section className="py-24">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-4">The person behind the practice</div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.95] text-[#1A1A1A]">
                How we came to be.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[14px] leading-relaxed text-[#1A1A1A]/70">
                A short version of a longer story &mdash; from a first digital brief in the Middle East to the practice we run today.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-10 items-start">
            {/* Portrait card */}
            <div className="col-span-12 md:col-span-5">
              <div className="rounded-[24px] bg-[#F4F2EC] border border-[#E8E4D9] overflow-hidden lift">
                <div
                  className="w-full bg-[#FBFAF6] bg-cover bg-center"
                  style={{
                    aspectRatio: '374 / 402',
                    backgroundImage: 'url(/team/ansar-lqip.jpg)',
                  }}
                >
                  <img
                    src="/team/ansar.jpg"
                    srcSet="/team/ansar.jpg 1x, /team/ansar@2x.jpg 2x"
                    alt="Ansar Kanhiroli, Chief Digital Officer and Managing Partner, DigitalWorks Consulting Group"
                    className="w-full h-full object-cover object-center"
                    width="374"
                    height="402"
                    data-testid="founder-portrait"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-6">
                  <div className="serif text-[26px] leading-tight text-[#1A1A1A]" data-testid="founder-name">Ansar Kanhiroli</div>
                  <div className="mono text-[11px] uppercase tracking-widest text-[#178838] mt-2">
                    Chief Digital Officer &amp; Managing Partner
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-[#1A1A1A]/50 mt-1">
                    DigitalWorks Consulting Group
                  </div>
                </div>
              </div>
            </div>

            {/* Story */}
            <div className="col-span-12 md:col-span-7">
              <div className="space-y-6 text-[16px] md:text-[17px] leading-[1.75] text-[#1A1A1A]/85">
                <p className="serif text-[22px] md:text-[26px] leading-[1.35] text-[#1A1A1A] font-medium">
                  DigitalWorks began in Dubai in August 2020 with a simple conviction: digital strategy is only as good as what actually gets built.
                </p>
                <p>
                  Before founding the company, Ansar had spent years on the inside of businesses across retail, banking, travel, loyalty and logistics &mdash; with career stints at Al-Futtaim Group, Riyad Bank, Landmark Group, McFadyen Consulting and IBS Group across the US, Europe and the Middle East. That journey gave him a perspective shaped not by consulting from the outside, but by actually building and running digital businesses, products and experiences.
                </p>
                <p>
                  Along the way, he had seen the same gap repeatedly: the people defining the strategy were often far removed from the people responsible for making it work. So DigitalWorks was built to close that gap &mdash; one team that could sit with leadership to define the opportunity, challenge the assumptions, and shape the product and technology, then stay close enough to build, launch and improve it. Strategy and execution, with accountability for the outcome.
                </p>
                <p>
                  What started as a boutique practice in Dubai grew into a distributed digital transformation and technology practice, working with organisations including Al-Futtaim, Sephora, Magrabi, Fenix Outdoor and other leading regional and global brands. The industries and scale evolved, but the operating principle stayed the same: senior people, close to the problem, moving from strategy to execution without the hand-off.
                </p>
                <p>
                  As AI began fundamentally changing what small, high-calibre teams could accomplish, we chose not to simply bolt new tools onto an old delivery model &mdash; we rebuilt the way we work, developing and running the model internally before bringing it to clients.
                </p>
                <p className="text-[#1A1A1A] font-medium">
                  Six years on, DigitalWorks remains true to its original idea: bringing strategy, technology and execution together to turn ambitious ideas into digital systems that actually work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
